import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DigitalEmployee, Industry } from '../types';
import TeslaRobot from './TeslaRobot';
import TeslaEmployeeCard from './TeslaEmployeeCard';

interface IndustryBrowserProps {
  employees: DigitalEmployee[];
}

const IndustryBrowser: React.FC<IndustryBrowserProps> = ({ employees }) => {
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  
  // Get all available industries
  const industries: Industry[] = ['Real Estate', 'Education', 'Transportation', 'Hospitality', 'Finance'];
  
  // Filter employees by selected industry
  const filteredEmployees = selectedIndustry 
    ? employees.filter(employee => employee.industries.includes(selectedIndustry))
    : [];
  
  // Get count of employees in each industry
  const getIndustryCount = (industry: Industry): number => {
    return employees.filter(employee => employee.industries.includes(industry)).length;
  };
  
  // Get a unique icon for each industry
  const getIndustryIcon = (industry: Industry): string => {
    const iconMap: Record<Industry, string> = {
      'Real Estate': '🏢',
      'Education': '🎓',
      'Transportation': '🚗',
      'Hospitality': '🏨',
      'Finance': '💰'
    };
    
    return iconMap[industry];
  };
  
  // Get a unique color for each industry
  const getIndustryColor = (industry: Industry): string => {
    const colorMap: Record<Industry, string> = {
      'Real Estate': '#4a6bff',
      'Education': '#6e3adc',
      'Transportation': '#00f7ff',
      'Hospitality': '#ff4757',
      'Finance': '#ffbc42'
    };
    
    return colorMap[industry];
  };
  
  return (
    <div className="industry-browser">
      <div className="industry-header">
        <h2 className="section-title">Browse by <span className="text-gradient">Industry</span></h2>
        <p className="section-subtitle">Find AI employees with expertise in specific industries</p>
      </div>
      
      <div className="industries-grid">
        {industries.map(industry => (
          <div 
            key={industry}
            className={`industry-card ${selectedIndustry === industry ? 'active' : ''}`}
            onClick={() => setSelectedIndustry(industry)}
            style={{ 
              '--industry-color': getIndustryColor(industry),
              '--industry-glow': `0 0 15px ${getIndustryColor(industry)}80`
            } as React.CSSProperties}
          >
            <div className="industry-icon">{getIndustryIcon(industry)}</div>
            <h3>{industry}</h3>
            <span className="industry-count">{getIndustryCount(industry)} employees</span>
            <div className="industry-hover-effect"></div>
          </div>
        ))}
      </div>
      
      {selectedIndustry && (
        <div className="industry-results">
          <div className="results-header">
            <h3>
              <span className="industry-icon-small">{getIndustryIcon(selectedIndustry)}</span>
              <span style={{ color: getIndustryColor(selectedIndustry) }}>{selectedIndustry}</span> Industry Specialists
            </h3>
            <button 
              className="clear-industry"
              onClick={() => setSelectedIndustry(null)}
            >
              Clear
            </button>
          </div>
          
          {filteredEmployees.length > 0 ? (
            <div className="industry-employees-grid">
              {filteredEmployees.map(employee => (
                <TeslaEmployeeCard 
                  key={employee.id} 
                  employee={employee} 
                  className="industry-employee-card"
                />
              ))}
            </div>
          ) : (
            <div className="no-industry-results">
              <p>No employees found specializing in this industry</p>
              <Link to="/search" className="browse-all-link">
                Search all employees
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IndustryBrowser;
